/* eslint-disable @typescript-eslint/no-empty-function */
import Backdrop from "@mui/material/Backdrop"
import CircularProgress from "@mui/material/CircularProgress"
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useEffect,
  useState,
} from "react"
import { useProfile } from "src/hooks"
import { CustomerDetailModel } from "src/types/customer"
import { getWithExpiry } from "src/utils/localStorages"

export interface AuthContextData {
  profile?: CustomerDetailModel
  onConfirmPhoneNumber: (
    phoneNumber: string
  ) => Promise<CustomerDetailModel | undefined>
  isProfileLoading?: boolean
  hasAuthenticated?: boolean
  handleLogout: () => void
  setHasAuthenticated: React.Dispatch<React.SetStateAction<boolean>>
}

const defaultAuthContextData: AuthContextData = {
  profile: undefined,
  hasAuthenticated: false,
  setHasAuthenticated: () => {},
  handleLogout: () => {},
  onConfirmPhoneNumber: () => Promise.resolve(undefined),
  isProfileLoading: false,
}

export const AuthContext = createContext<AuthContextData>(
  defaultAuthContextData
)
const AuthProvider = ({ children }: PropsWithChildren<unknown>) => {
  const { onGetProfile, isProfileLoading } = useProfile()
  const [profile, setProfile] = useState<CustomerDetailModel | undefined>(
    undefined
  )
  const [showBackdrop, setShowBackdrop] = useState(false)
  const [hasAuthenticated, setHasAuthenticated] = useState(false)

  const onConfirmPhoneNumber = useCallback(
    async (val: string) => {
      const res = await onGetProfile(val)
      if (res) {
        setProfile(res)
      }
      return res
    },
    [onGetProfile]
  )

  const handleLogout = useCallback(() => {
    setShowBackdrop(true)
    setTimeout(() => {
      setHasAuthenticated(false)
      sessionStorage.removeItem("phoneNumber")
      setShowBackdrop(false)
    }, 200)
  }, [setHasAuthenticated])

  useEffect(() => {
    async function fetchProfile(p: string) {
      const res = await onGetProfile(p)
      setHasAuthenticated(!!res)
      setProfile(res)
    }
    const phoneNumberCache = getWithExpiry("phoneNumber")
    if (!profile && phoneNumberCache) {
      fetchProfile(phoneNumberCache)
    }
  }, [profile, onGetProfile])

  return (
    <AuthContext.Provider
      value={{
        profile,
        handleLogout,
        isProfileLoading,
        onConfirmPhoneNumber,
        setHasAuthenticated,
        hasAuthenticated,
      }}
    >
      {children}
      <Backdrop
        sx={{ color: "#fff", zIndex: theme => theme.zIndex.drawer + 1 }}
        open={showBackdrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </AuthContext.Provider>
  )
}

export default AuthProvider
